import { useState } from 'react'
import { HelpCircle, MessageSquare, Check, X, Loader2, ToggleLeft, ToggleRight } from 'lucide-react'
import { SupabaseClient } from '@supabase/supabase-js'
import { useQuizzes, useGreetings, Quiz, Greeting } from '@/lib/data/hooks/useContent'

interface ContentViewProps {
  client: SupabaseClient | null
}

type Tab = 'quizzes' | 'greetings'

export function ContentView({ client }: ContentViewProps) {
  const [activeTab, setActiveTab] = useState<Tab>('quizzes')

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-[var(--text-muted)]">
        <HelpCircle size={48} className="mb-4 opacity-20" />
        <p className="text-lg font-medium">Not Connected</p>
        <p className="text-sm">Configure Supabase to manage content</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-[var(--text-main)]">Content Management</h2>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[var(--border)]">
        <button
          onClick={() => setActiveTab('quizzes')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'quizzes'
              ? 'border-[var(--primary)] text-[var(--primary)]'
              : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-main)]'
          }`}
        >
          <HelpCircle size={18} />
          Quizzes
        </button>
        <button
          onClick={() => setActiveTab('greetings')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'greetings'
              ? 'border-[var(--primary)] text-[var(--primary)]'
              : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-main)]'
          }`}
        >
          <MessageSquare size={18} />
          Greetings
        </button>
      </div>

      {/* Content */}
      {activeTab === 'quizzes' ? (
        <QuizzesTab client={client} />
      ) : (
        <GreetingsTab client={client} />
      )}
    </div>
  )
}

function QuizzesTab({ client }: { client: SupabaseClient }) {
  const { quizzes, isLoading, error, toggleQuiz } = useQuizzes(client)
  const [toggling, setToggling] = useState<string | null>(null)

  const handleToggle = async (quiz: Quiz) => {
    setToggling(quiz.id)
    await toggleQuiz(quiz.id, !quiz.active)
    setToggling(null)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg">
        {error}
      </div>
    )
  }

  const activeCount = quizzes.filter((q) => q.active).length

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 text-sm text-[var(--text-muted)]">
        <span>{quizzes.length} total quizzes</span>
        <span className="text-emerald-400">{activeCount} active</span>
        <span className="text-gray-400">{quizzes.length - activeCount} inactive</span>
      </div>

      <div className="space-y-3">
        {quizzes.map((quiz) => (
          <div
            key={quiz.id}
            className={`bg-[var(--bg-card)] border rounded-xl p-4 transition-colors ${
              quiz.active ? 'border-emerald-500/30' : 'border-[var(--border)]'
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-[var(--text-main)] font-medium mb-3">{quiz.question}</p>

                <div className="grid grid-cols-2 gap-2 mb-3">
                  {quiz.answers.map((answer, index) => (
                    <div
                      key={index}
                      className={`p-2 rounded-lg text-sm flex items-center gap-2 ${
                        index === quiz.correct_index
                          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
                          : 'bg-[var(--bg-element)] text-[var(--text-muted)]'
                      }`}
                    >
                      {index === quiz.correct_index && <Check size={14} />}
                      {answer}
                    </div>
                  ))}
                </div>

                {quiz.categories && quiz.categories.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {quiz.categories.map((cat, i) => (
                      <span
                        key={i}
                        className="px-2 py-0.5 bg-[var(--bg-element)] text-[var(--text-muted)] text-xs rounded-full"
                      >
                        {cat}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => handleToggle(quiz)}
                disabled={toggling === quiz.id}
                className={`p-2 rounded-lg transition-colors ${
                  quiz.active
                    ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                    : 'bg-[var(--bg-element)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                {toggling === quiz.id ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : quiz.active ? (
                  <ToggleRight size={20} />
                ) : (
                  <ToggleLeft size={20} />
                )}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function GreetingsTab({ client }: { client: SupabaseClient }) {
  const { greetings, isLoading, error, toggleGreeting } = useGreetings(client)
  const [toggling, setToggling] = useState<string | null>(null)

  const handleToggle = async (greeting: Greeting) => {
    setToggling(greeting.id)
    await toggleGreeting(greeting.id, !greeting.active)
    setToggling(null)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg">
        {error}
      </div>
    )
  }

  const activeCount = greetings.filter((g) => g.active).length

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 text-sm text-[var(--text-muted)]">
        <span>{greetings.length} total greetings</span>
        <span className="text-emerald-400">{activeCount} active</span>
        <span className="text-gray-400">{greetings.length - activeCount} inactive</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {greetings.map((greeting) => (
          <div
            key={greeting.id}
            className={`bg-[var(--bg-card)] border rounded-xl p-4 transition-colors ${
              greeting.active ? 'border-emerald-500/30' : 'border-[var(--border)]'
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <p className="text-[var(--text-main)] text-sm flex-1">{greeting.greeting}</p>

              <button
                onClick={() => handleToggle(greeting)}
                disabled={toggling === greeting.id}
                className={`p-2 rounded-lg transition-colors flex-shrink-0 ${
                  greeting.active
                    ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
                    : 'bg-[var(--bg-element)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                {toggling === greeting.id ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : greeting.active ? (
                  <ToggleRight size={18} />
                ) : (
                  <ToggleLeft size={18} />
                )}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
